import { Component, OnInit, ElementRef, ViewChild, HostListener, signal, inject } from '@angular/core';
import { NgIf } from '@angular/common';
import { RouterLink } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { ScrollState } from '../../services/scroll-state';
import { PostService, Post } from '../../services/post';
import { TestimonialService, Testimonial } from '../../services/testimonial';
import { BookingModal } from '../../components/booking-modal/booking-modal';
import { MetaService } from '../../services/meta.service';
import { SiteSettingsService } from '../../services/site-settings.service';
import { environment } from '../../../environments/environment';

interface FeaturedService {
  id: number;
  service_name: string;
  tagline: string;
  short_description: string;
  description: string;
  pricing: number;
  duration_minutes: number;
  duration_formatted: string;
  image_path: string;
  image_url: string;
  slug: string;
}

@Component({
  selector: 'app-home',
  imports: [NgIf, RouterLink, BookingModal],
  templateUrl: './home.html',
  styleUrl: './home.css',
})
export class Home implements OnInit {
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  isPlaying = true;
  scrollProgress = 0; // 0 to 1, tracks scroll progress for video expansion

  // Blog signals
  latestBlogs = signal<Post[]>([]);
  blogsLoading = signal<boolean>(true);

  // Booking modal
  showBookingModal = signal(false);

  // Featured services
  featuredServices = signal<FeaturedService[]>([]);
  servicesLoading = signal(true);
  private storageUrl = environment.storageUrl;
  private http = inject(HttpClient);
  private settings = inject(SiteSettingsService);

  // Site Settings — already loaded via APP_INITIALIZER, no flash
  heroTitle1 = signal(this.settings.val('home_hero', 'home_hero_title_line1', 'Transform Your'));
  heroTitle2 = signal(this.settings.val('home_hero', 'home_hero_title_line2', 'Confidence'));
  heroDescription = signal(this.settings.val('home_hero', 'home_hero_description', 'Experience world-class hair transplantation, PRP therapy, and scalp micropigmentation with cutting-edge technology and expert care.'));
  heroPhone = signal(this.settings.val('home_hero', 'home_hero_phone', '+91 98765 43210'));

  servicesTitle = signal(this.settings.val('home_services', 'home_services_title', 'Our Services'));
  servicesDescription = signal(this.settings.val('home_services', 'home_services_description', 'Discover our premium hair restoration solutions designed for exceptional results'));

  ctaTagline = signal(this.settings.val('home_cta', 'home_cta_tagline', 'Start Your Journey'));
  ctaTitleLine1 = signal(this.settings.val('home_cta', 'home_cta_title_line1', 'Begin Your'));
  ctaTitleLine2 = signal(this.settings.val('home_cta', 'home_cta_title_line2', 'Transformation'));
  ctaDescription = signal(this.settings.val('home_cta', 'home_cta_description', 'Every journey starts with a conversation. Schedule a complimentary consultation with our specialists to discuss your unique needs.'));

  blogTagline = signal(this.settings.val('home_blog', 'home_blog_tagline', 'Latest Insights'));
  blogTitle = signal(this.settings.val('home_blog', 'home_blog_title', 'Expert Hair Care Tips'));
  blogDescription = signal(this.settings.val('home_blog', 'home_blog_description', 'Stay informed with the latest trends, tips, and insights from our hair restoration experts'));

  contactTagline = signal(this.settings.val('home_contact', 'home_contact_tagline', 'Get In Touch'));
  contactTitle = signal(this.settings.val('home_contact', 'home_contact_title', 'Visit Our Studio'));
  contactDescription = signal(this.settings.val('home_contact', 'home_contact_description', 'Experience world-class hair restoration services in our state-of-the-art facility'));
  contactPhone = signal(this.settings.val('contact', 'contact_phone', '+91 98765 43210'));
  contactEmail = signal(this.settings.val('contact', 'contact_email', 'contact@oghairstudio.com'));
  contactAddressLine1 = signal(this.settings.val('contact', 'contact_address_line1', 'Style On Studio'));
  contactAddressLine2 = signal(this.settings.val('contact', 'contact_address_line2', 'Hyderabad, Telangana, India'));
  contactHours = signal(this.settings.val('contact', 'contact_hours', 'Mon - Sat: 9:00 AM - 8:00 PM'));
  contactMapEmbedUrl = signal(this.settings.val('contact', 'contact_map_embed', 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30450.281957411953!2d78.35258718869166!3d17.44605634189847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb913d5b993123%3A0x3b331b9ff229cda5!2sStyle%20On%20Studio!5e0!3m2!1sen!2sin!4v1764448152057!5m2!1sen!2sin'));

  private sanitizer = inject(DomSanitizer);

  // Testimonials (will be loaded dynamically)
  column1: Testimonial[] = [];
  column2: Testimonial[] = [];
  column3: Testimonial[] = [];
  mobileTestimonials: Testimonial[] = [];

  constructor(
    private scrollStateService: ScrollState,
    private blogService: PostService,
    private testimonialService: TestimonialService
  ) {}

  @HostListener('window:scroll', [])
  onWindowScroll() {
    // Calculate scroll progress (0 to 1) over first 80px of scroll
    const scrollY = window.scrollY;
    this.scrollProgress = Math.min(scrollY / 80, 1);
    this.scrollStateService.setScrollProgress(this.scrollProgress);
  }

  private metaService = inject(MetaService);

  ngOnInit() {
    // Set page meta tags
    this.metaService.setPageMeta('home');

    // Ensure video plays with multiple fallback attempts
    this.playVideo();

    // Additional attempt after short delay
    setTimeout(() => this.playVideo(), 500);

    // Final attempt after page fully loads
    setTimeout(() => this.playVideo(), 1500);

    // Load testimonials from API
    this.loadTestimonials();

    // Load latest blogs
    this.loadLatestBlogs();

    // Load featured services
    this.loadFeaturedServices();

    // Add touch/click listener for mobile devices to play video
    if (typeof document !== 'undefined') {
      const playOnInteraction = () => {
        this.playVideo();
        document.removeEventListener('touchstart', playOnInteraction);
        document.removeEventListener('click', playOnInteraction);
      };
      document.addEventListener('touchstart', playOnInteraction, { once: true });
      document.addEventListener('click', playOnInteraction, { once: true });
    }
  }

  loadTestimonials() {
    this.testimonialService.getActive().subscribe({
      next: (response) => {
        if (response.status && response.data) {
          const testimonials = response.data;

          // Distribute testimonials across 3 columns for desktop
          this.column1 = testimonials.filter((_, index) => index % 3 === 0);
          this.column2 = testimonials.filter((_, index) => index % 3 === 1);
          this.column3 = testimonials.filter((_, index) => index % 3 === 2);

          // Combine all for mobile view
          this.mobileTestimonials = testimonials;
        }
      },
      error: (error) => {
        console.error('Error loading testimonials:', error);
      }
    });
  }

  loadLatestBlogs() {
    this.blogsLoading.set(true);
    this.blogService.getPublishedBlogs({ per_page: 3 }).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          // Handle both paginated response (data.posts) and direct array response
          const posts = response.data.posts || (Array.isArray(response.data) ? response.data : []);
          this.latestBlogs.set(posts.slice(0, 3));
        }
        this.blogsLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading blogs:', error);
        this.blogsLoading.set(false);
      }
    });
  }

  getImageUrl(path: string): string {
    return path || '/images/placeholder-blog.jpg';
  }

  getServiceImageUrl(service: FeaturedService): string {
    if (service.image_url) return service.image_url;
    if (!service.image_path) return '/images/service-placeholder.jpg';
    if (service.image_path.startsWith('http')) return service.image_path;
    return `${this.storageUrl}/${service.image_path}`;
  }

  loadFeaturedServices() {
    this.servicesLoading.set(true);
    this.http.get<any>(`${environment.apiUrl}/public/services/featured`).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.featuredServices.set(response.data.slice(0, 3));
        }
        this.servicesLoading.set(false);
      },
      error: () => {
        this.servicesLoading.set(false);
      }
    });
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  playVideo() {
    const video = this.heroVideo?.nativeElement;
    if (video) {
      video.muted = true; // Ensure muted for autoplay
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');

      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this.isPlaying = true;
          })
          .catch(err => {
            console.log('Video autoplay attempt:', err);
            this.isPlaying = false;
          });
      }
    }
  }

  toggleVideo() {
    const video = this.heroVideo?.nativeElement;
    if (video) {
      if (this.isPlaying) {
        video.pause();
        this.isPlaying = false;
      } else {
        video.play().then(() => {
          this.isPlaying = true;
        }).catch(err => {
          console.log('Video play failed:', err);
        });
      }
    }
  }

  openBookingModal() {
    this.showBookingModal.set(true);
  }

  closeBookingModal() {
    this.showBookingModal.set(false);
  }

  contactMapEmbed(): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.contactMapEmbedUrl());
  }
}
